import React from "react";
import { Table, Button, Input, Select, Form } from "antd";
import {
  LeftOutlined,
  SearchOutlined,
  DeleteOutlined,
  MenuOutlined,
  CaretDownOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import PageLogoutButton from "../../Components/PageLogoutButton";

// --- 1. Types & Mock Data ---
interface OrderItem {
  key: string;
  vendor: string;
  model: string;
  qty: number;
  manu: string;
  vendorShipped: string;
  tracking: string;
  shipTo: string;
}

const dataSource: OrderItem[] = [
  {
    key: "1",
    vendor: "DBS",
    model: "6260B",
    qty: 2,
    manu: "Agilent / HP",
    vendorShipped: "26 Dec 2012",
    tracking: "Pro # 1610313767",
    shipTo: "Drop Ship to Cus",
  },
  {
    key: "2",
    vendor: "DBS",
    model: "6260B",
    qty: 2,
    manu: "Agilent / HP",
    vendorShipped: "26 Dec 2012",
    tracking: "Pro # 1610313767",
    shipTo: "Drop Ship to Cus",
  },
  {
    key: "3",
    vendor: "DBS",
    model: "6260B",
    qty: 2,
    manu: "Agilent / HP",
    vendorShipped: "26 Dec 2012",
    tracking: "Pro # 1610313767",
    shipTo: "Drop Ship to Cus",
  },
];

const OrderCreate = () => {
  const navigate = useNavigate();

  // --- 2. Table Column Definitions ---
  const columns: ColumnsType<OrderItem> = [
    {
      title: "#",
      key: "index",
      render: (_, __, index) => index + 1,
      onHeaderCell: () => ({
        style: { backgroundColor: "#DFEFFF", color: "black" },
      }),
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      key: "vendor",
      onHeaderCell: () => ({
        style: { backgroundColor: "#DFEFFF", color: "black" },
      }),
    },
    {
      title: "Model",
      dataIndex: "model",
      key: "model",
      onHeaderCell: () => ({
        style: { backgroundColor: "#DFEFFF", color: "black" },
      }),
    },
    {
      title: "Qty",
      dataIndex: "qty",
      key: "qty",
      onHeaderCell: () => ({
        style: { backgroundColor: "#DFEFFF", color: "black" },
      }),
    },
    {
      title: "Manu",
      dataIndex: "manu",
      key: "manu",
      onHeaderCell: () => ({
        style: { backgroundColor: "#DFEFFF", color: "black" },
      }),
    },
    {
      title: "Vendor Shipped",
      dataIndex: "vendorShipped",
      key: "vendorShipped",
      onHeaderCell: () => ({
        style: { backgroundColor: "#DFEFFF", color: "black" },
      }),
    },
    {
      title: "Tracking Number",
      dataIndex: "tracking",
      key: "tracking",
      onHeaderCell: () => ({
        style: { backgroundColor: "#DFEFFF", color: "black" },
      }),
    },
    {
      title: "Vendor ship to",
      dataIndex: "shipTo",
      key: "shipTo",
      onHeaderCell: () => ({
        style: { backgroundColor: "#DFEFFF", color: "black" },
      }),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      onHeaderCell: () => ({
        style: { backgroundColor: "#DFEFFF", color: "black" },
      }),
      render: () => (
        <DeleteOutlined className="text-gray-400 hover:text-red-500 cursor-pointer text-lg" />
      ),
    },
  ];

  return (
    <div className="p-2 bg-slate-50 min-h-screen">
      {/* 3. Header Section */}
      <div className="flex justify-between items-center mb-8 px-2 gap-x-4">
        <div className="flex items-center gap-3 flex-shrink-0">
          <Button
            icon={<LeftOutlined />}
            className="flex items-center justify-center rounded-md border-gray-200"
            onClick={() => navigate("/open-order")} // Moves back to Open Order Report
          />
          <h1 className="text-xl font-normal text-gray-700">Order Create</h1>
        </div>

        <div className="flex items-center gap-3 flex-1 justify-end">
          <Input
            prefix={<SearchOutlined className="text-xl" />}
            placeholder="Search ...."
            className="rounded-lg h-10 border-gray-300 shadow-sm flex-1 max-w-xs"
          />
          <PageLogoutButton />
        </div>
      </div>

      {/* 4. Info Cards Section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
        {/* Customer Info Card */}
        <div className="lg:col-span-4 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <h2 className="text-gray-600 font-medium mb-6">Customer Info</h2>
          <Form layout="vertical">
            <Form.Item label="Customer">
              <Input placeholder="Boyd Cockrill" className="Allinput" />
            </Form.Item>
            <Form.Item label="Last Update">
              <Input placeholder="8 Jan 2013" className="Allinput" />
            </Form.Item>
          </Form>
        </div>

        {/* Order Summary Card */}
        <div className="lg:col-span-8 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <h2 className="text-gray-600 font-medium mb-6">Order Summary</h2>
          <Form layout="vertical">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <Form.Item label="Quote ID">
                <Input placeholder="27000" className="Allinput" />
              </Form.Item>
              <Form.Item label="Status">
                <Select
                  placeholder="Closed"
                  className="h-10 rounded-lg"
                  suffixIcon={<CaretDownOutlined />}
                />
              </Form.Item>
              <Form.Item label="SO Date">
                <Input placeholder="16 Nov 2012" className="Allinput" />
              </Form.Item>
              <Form.Item label="Priority">
                <Input placeholder="Standard" className="Allinput" />
              </Form.Item>
            </div>
          </Form>
        </div>
      </div>

      {/* 5. Sub-Actions Bar */}
      <div className="flex justify-end items-center gap-4 mb-6">
        <Select
          placeholder="Vendor"
          className="w-32 h-10 rounded-lg"
          suffixIcon={<CaretDownOutlined />}
        />
        <Button
          type="primary"
          icon={<MenuOutlined />}
          className="bg-[#004aad] hover:bg-blue-800 h-10 px-6 rounded-lg border-none flex items-center font-light"
        >
          Summary
        </Button>
      </div>

      {/* 6. Line Items Table */}
      {/* <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden"> */}
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        rowClassName={() => "h-16"}
        className="custom-table"
        scroll={{ x: "max-content" }}
      />
      {/* </div> */}
    </div>
  );
};

export default OrderCreate;
